import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, ClockIcon, UserIcon, MessageSquareIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon } from 'lucide-react';
interface TimelineEvent {
  id: string;
  timestamp: string;
  type: 'created' | 'updated' | 'approved' | 'rejected' | 'comment';
  user: string;
  description: string;
  comment?: string;
}
interface Comment {
  id: string;
  user: string;
  role: string;
  timestamp: string;
  message: string;
}
interface RequestDetail {
  id: string;
  type: string;
  description: string;
  requestedBy: string;
  requestedAt: string;
  status: string;
  details: {
    paymentMethod?: string;
    roleName?: string;
    reason?: string;
    permissions?: string;
    location?: string;
    brand?: string;
    model?: string;
    terminalName?: string;
    rejectionReason?: string;
  };
  timeline: TimelineEvent[];
  comments: Comment[];
}
export function RequestDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [newComment, setNewComment] = useState('');
  // Mock data - in real app, fetch based on id
  const [request, setRequest] = useState<RequestDetail>(() => {
    const now = new Date();
    if (location.state && typeof location.state === 'object' && 'location' in location.state && 'brand' in location.state && 'model' in location.state && 'terminalName' in location.state) {
      const terminalData = location.state as {
        location: string;
        brand: string;
        model: string;
        terminalName: string;
      };
      return {
        id: id || 'REQ-001',
        type: 'Terminal Request',
        description: `Request for Terminal ${terminalData.terminalName} Activation`,
        requestedBy: 'Simon Lee',
        requestedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'Pending',
        details: {
          location: terminalData.location,
          brand: terminalData.brand,
          model: terminalData.model,
          terminalName: terminalData.terminalName
        },
        timeline: [{
          id: 'evt-1',
          timestamp: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'created',
          user: 'Simon Lee',
          description: 'Request created'
        }, {
          id: 'evt-2',
          timestamp: new Date(now.getTime() - 1.5 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'comment',
          user: 'Admin User',
          description: 'Comment added',
          comment: 'We are reviewing your terminal request. Additional documentation may be required.'
        }, {
          id: 'evt-3',
          timestamp: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'updated',
          user: 'System',
          description: 'Request assigned to terminal team'
        }],
        comments: [{
          id: 'cmt-1',
          user: 'Admin User',
          role: 'Administrator',
          timestamp: new Date(now.getTime() - 1.5 * 24 * 60 * 60 * 1000).toISOString(),
          message: 'We are reviewing your terminal request. Additional documentation may be required.'
        }]
      };
    }
    return {
      id: id || 'REQ-001',
      type: 'Payment Method Activation',
      description: 'Request to activate ShopeePay payment method',
      requestedBy: 'Simon Lee',
      requestedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        paymentMethod: 'ShopeePay',
        reason: 'Customer demand for ShopeePay payments'
      },
      timeline: [{
        id: 'evt-1',
        timestamp: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'created',
        user: 'Simon Lee',
        description: 'Request created'
      }, {
        id: 'evt-2',
        timestamp: new Date(now.getTime() - 1.5 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'comment',
        user: 'Admin User',
        description: 'Comment added',
        comment: 'We are reviewing your request. Additional documentation may be required.'
      }, {
        id: 'evt-3',
        timestamp: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'updated',
        user: 'System',
        description: 'Request assigned to compliance team'
      }],
      comments: [{
        id: 'cmt-1',
        user: 'Admin User',
        role: 'Administrator',
        timestamp: new Date(now.getTime() - 1.5 * 24 * 60 * 60 * 1000).toISOString(),
        message: 'We are reviewing your request. Additional documentation may be required.'
      }]
    };
  });
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Closed':
        return 'bg-gray-100 text-gray-800';
      case 'Pending':
        return 'bg-amber-100 text-amber-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Closed':
        return <CheckCircleIcon size={20} className="text-gray-600" data-id="element-14527" data-mp="3ru9qzx3q3gof" />;
      case 'Pending':
        return <AlertCircleIcon size={20} className="text-amber-600" data-id="element-14528" data-mp="1m00aw2ejoeyg" />;
      case 'In Progress':
        return <ClockIcon size={20} className="text-blue-600" data-id="element-14529" data-mp="1q253qyx657k1" />;
      default:
        return <ClockIcon size={20} className="text-gray-600" data-id="element-14530" data-mp="2yyk2ewoareu3" />;
    }
  };
  const getTimelineIcon = (type: string) => {
    switch (type) {
      case 'created':
        return <CheckCircleIcon size={16} className="text-blue-600" data-id="element-14531" data-mp="2tye796itja79" />;
      case 'approved':
        return <CheckCircleIcon size={16} className="text-green-600" data-id="element-14532" data-mp="2pyngv3x7ew39" />;
      case 'rejected':
        return <XCircleIcon size={16} className="text-red-600" data-id="element-14533" data-mp="2l0lotthsvmfn" />;
      case 'comment':
        return <MessageSquareIcon size={16} className="text-purple-600" data-id="element-14534" data-mp="3ho043nbpxdc3" />;
      default:
        return <ClockIcon size={16} className="text-gray-600" data-id="element-14535" data-mp="3dorb4koi5jhl" />;
    }
  };
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const handleAddComment = () => {
    if (!newComment.trim()) return;
    const now = new Date();
    const comment: Comment = {
      id: `cmt-${Date.now()}`,
      user: 'Simon Lee',
      role: 'Owner',
      timestamp: now.toISOString(),
      message: newComment
    };
    const timelineEvent: TimelineEvent = {
      id: `evt-${Date.now()}`,
      timestamp: now.toISOString(),
      type: 'comment',
      user: 'Simon Lee',
      description: 'Comment added',
      comment: newComment
    };
    setRequest({
      ...request,
      comments: [...request.comments, comment],
      timeline: [...request.timeline, timelineEvent].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    });
    setNewComment('');
  };
  return <div className="max-w-7xl mx-auto py-8 px-4" data-id="element-14536" data-mp="10ib7m6fv86gl">
      {/* Header */}
      <div className="mb-6" data-id="element-14537" data-mp="15gl0ubi14wzk">
        <button onClick={() => navigate('/requests')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4" data-id="element-14538" data-mp="15erwj2uw2ouq">
          <ArrowLeftIcon size={20} data-id="element-14539" data-mp="1eghvctr3oq2j" />
          <span data-id="element-14540" data-mp="1ehjb3bj2a0ql">Back to Requests</span>
        </button>
        <div className="flex items-start justify-between" data-id="element-14541" data-mp="1zrg4tzbsg45l">
          <div data-id="element-14542" data-mp="1zpvaa9ph4l7q">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-14543" data-mp="1ztj4lb2qtbni">
              {request.id}
            </h1>
            <p className="text-gray-600" data-id="element-14544" data-mp="29pvvy2atfdf3">{request.description}</p>
          </div>
          <div className="flex items-center gap-2" data-id="element-14545" data-mp="29nt7hmpb9sch">
            {getStatusIcon(request.status)}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`} data-id="element-14546" data-mp="24oq1o01ibi3g">
              {request.status}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-14547" data-mp="1qr9tok3kbg9l">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6" data-id="element-14548" data-mp="1lzrwm74ntztw">
          {/* Request Details */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-14549" data-mp="yah2h8htf3oq">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-14550" data-mp="tbnn1aiyx2lx">
              <h3 className="text-md font-semibold text-gray-900" data-id="element-14551" data-mp="t9jkox0pwbnr">
                Request Details
              </h3>
            </div>
            <div className="p-6 space-y-4" data-id="element-14552" data-mp="39pfxy3uxoydx">
              <div className="grid grid-cols-2 gap-4" data-id="element-14553" data-mp="39rai3fydsmgy">
                <div data-id="element-14554" data-mp="2v1bmnky7np56">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14555" data-mp="2v1urq12rij1s">
                    Type
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14556" data-mp="300668p405hqc">
                    {request.type}
                  </p>
                </div>
                <div data-id="element-14557" data-mp="3sp3jgdtcwoc8">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14558" data-mp="3snimyinhevbq">
                    Requested By
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14559" data-mp="3er587hac180y">
                    {request.requestedBy}
                  </p>
                </div>
                <div data-id="element-14560" data-mp="238klqx5l1c9l">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14561" data-mp="2382l7obw0bvv">
                    Requested At
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14562" data-mp="2v50ekzl0b52w">
                    {formatTimestamp(request.requestedAt)}
                  </p>
                </div>
                <div data-id="element-14563" data-mp="305xut6mghp1r">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14564" data-mp="2mcexdnx0x6g6">
                    Status
                  </p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`} data-id="element-14565" data-mp="2qcyvatpf8e3d">
                    {request.status}
                  </span>
                </div>
              </div>

              {request.details.paymentMethod && <div data-id="element-14566" data-mp="23fnjju093jji">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14567" data-mp="27f65mt3t60nx">
                    Payment Method
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14568" data-mp="1niv4shyn4dj">
                    {request.details.paymentMethod}
                  </p>
                </div>}

              {request.details.roleName && <div data-id="element-14569" data-mp="1q63hxr3rz8qx">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14570" data-mp="1m48iyt9bh7gb">
                    Role Name
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14571" data-mp="1zwoqa6993rn1">
                    {request.details.roleName}
                  </p>
                </div>}

              {request.details.reason && <div data-id="element-14572" data-mp="29s9lnljtdtnt">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14573" data-mp="29vxf6kp9eg2j">
                    Reason
                  </p>
                  <p className="text-sm text-gray-700" data-id="element-14574" data-mp="2iv10lvbwf7k7">
                    {request.details.reason}
                  </p>
                </div>}

              {request.details.permissions && <div data-id="element-14575" data-mp="ofonggpa1uer">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14576" data-mp="oh9jybv5jnf9">
                    Permissions
                  </p>
                  <p className="text-sm text-gray-700" data-id="element-14577" data-mp="jjrjl9v7agiv">
                    {request.details.permissions}
                  </p>
                </div>}

              {request.details.location && <div data-id="element-14578" data-mp="2thu64gvuucjl">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14579" data-mp="2thkmdb1el1mc">
                    Location
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14580" data-mp="2xkqr2e8vakqp">
                    {request.details.location}
                  </p>
                </div>}

              {request.details.brand && <div data-id="element-14581" data-mp="37f8wbntxmocm">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14582" data-mp="3c8k7u29ovtav">
                    Brand
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14583" data-mp="3h6ddmu72ylam">
                    {request.details.brand}
                  </p>
                </div>}

              {request.details.model && <div data-id="element-14584" data-mp="3q8mip4jkxmgm">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14585" data-mp="3q60ad2ov72xc">
                    Model
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14586" data-mp="19l52x5r2125k">
                    {request.details.model}
                  </p>
                </div>}

              {request.details.terminalName && <div data-id="element-14587" data-mp="10m06c4z1ziw1">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-14588" data-mp="10mt4ihjy502k">
                    Terminal Name
                  </p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-14589" data-mp="1t9w297gst6rr">
                    {request.details.terminalName}
                  </p>
                </div>}

              {request.details.rejectionReason && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-14590" data-mp="1jirouc6rttz1">
                  <p className="text-xs text-red-500 uppercase tracking-wider mb-1" data-id="element-14591" data-mp="2gzgktp91i20i">
                    Rejection Reason
                  </p>
                  <p className="text-sm text-red-700" data-id="element-14592" data-mp="2c770coqp1u5m">
                    {request.details.rejectionReason}
                  </p>
                </div>}
            </div>
          </div>

          {/* Comments Section */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-14593" data-mp="1mskgty9ei30m">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-14594" data-mp="1rrdw9w89043f">
              <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-14595" data-mp="1rrnkpfdna370">
                <MessageSquareIcon size={18} className="text-gray-600" data-id="element-14596" data-mp="2fhrqnteb2f16" />
                Comments ({request.comments.length})
              </h3>
            </div>
            <div className="p-6 space-y-4" data-id="element-14597" data-mp="25ryg8vpwg5yi">
              {request.comments.length === 0 ? <p className="text-sm text-gray-500 text-center py-4" data-id="element-14598" data-mp="2apgf1ta7d4ss">
                  No comments yet. Be the first to comment!
                </p> : <div className="space-y-4" data-id="element-14599" data-mp="336kdn0cy9cjn">
                  {request.comments.map(comment => <div key={comment.id} className="border border-gray-200 rounded-lg p-4" data-id="element-14600" data-mp="3clco4zjqv6vs">
                      <div className="flex items-start gap-3" data-id="element-14601" data-mp="38j9bi7lc9w8x">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold flex-shrink-0" data-id="element-14602" data-mp="3mbfgcxj1kgt2">
                          {comment.user.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="flex-1" data-id="element-14603" data-mp="2pmzrl3nrabh0">
                          <div className="flex items-center justify-between mb-1" data-id="element-14604" data-mp="33ky1nwv1nlqp">
                            <div data-id="element-14605" data-mp="2yl1y1yq9dwbo">
                              <p className="text-sm font-medium text-gray-900" data-id="element-14606" data-mp="2yktmqb9saz2l">
                                {comment.user}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-14607" data-mp="dvm8t86oufo5">
                                {comment.role}
                              </p>
                            </div>
                            <p className="text-xs text-gray-500" data-id="element-14608" data-mp="pxnat13n2oho">
                              {formatTimestamp(comment.timestamp)}
                            </p>
                          </div>
                          <p className="text-sm text-gray-700" data-id="element-14609" data-mp="uux62xdpludf">
                            {comment.message}
                          </p>
                        </div>
                      </div>
                    </div>)}
                </div>}

              {/* Add Comment Form */}
              <div className="border-t border-gray-200 pt-4 mt-4" data-id="element-14610" data-mp="c6iikk2loyhd">
                <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" placeholder="Add a comment..." rows={3} value={newComment} onChange={e => setNewComment(e.target.value)} data-id="element-14611" data-mp="c45tlmyfdnut" />
                <div className="flex justify-end mt-2" data-id="element-14612" data-mp="3d212k2u4xyba">
                  <button onClick={handleAddComment} disabled={!newComment.trim()} className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md hover:bg-[#C41B14] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-14613" data-mp="382wujfiy66mj">
                    Add Comment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Sidebar */}
        <div className="lg:col-span-1" data-id="element-14614" data-mp="2kdlzmeoa36gb">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden sticky top-4" data-id="element-14615" data-mp="2kenaoj5ansy1">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-14616" data-mp="2fgc3yhaapyk1">
              <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-14617" data-mp="2afxx32kfkunl">
                <ClockIcon size={18} className="text-gray-600" data-id="element-14618" data-mp="26dufdw764ttv" />
                Timeline
              </h3>
            </div>
            <div className="p-6" data-id="element-14619" data-mp="3uc3krvqcackf">
              <div className="space-y-4" data-id="element-14620" data-mp="3u8xzkrktzutz">
                {request.timeline.map((event, index) => <div key={event.id} className="flex gap-3" data-id="element-14621" data-mp="324v9euqrfl8" data-mpi={index}>
                    <div className="flex flex-col items-center" data-id="element-14622" data-mp="3kc2zw5ctnoe4">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-14623" data-mp="3pb63rmh2foki">
                        {getTimelineIcon(event.type)}
                      </div>
                      {index < request.timeline.length - 1 && <div className="w-0.5 h-full bg-gray-200 mt-2" data-id="element-14624" data-mp="3gcu4kw24vigg" />}
                    </div>
                    <div className="flex-1 pb-4" data-id="element-14625" data-mp="36koh9zmttp7l">
                      <p className="text-sm font-medium text-gray-900" data-id="element-14626" data-mp="36lhg8efjnaf6">
                        {event.description}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-14627" data-mp="2wms6xa1pqyaj">
                        {event.user} • {formatTimestamp(event.timestamp)}
                      </p>
                      {event.comment && <p className="text-xs text-gray-600 mt-2 bg-gray-50 p-2 rounded" data-id="element-14628" data-mp="2906euvhyb23t">
                          {event.comment}
                        </p>}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}